import { CoralogixRum } from '@coralogix/browser';

import { Logger } from 'src/services/Logger';
import { getUserMetadata } from 'src/services/UserService';

import { getUserTenant } from '../TenantsService';

// Environment configuration
const coralogixPublicKey = import.meta.env.VITE_CORALOGIX_RUM_PUBLIC_KEY;
const coralogixApplicationName = 'product-hub';
const coralogixVersion = UI_VERSION || 'unknown';
const coralogixEnvironment = import.meta.env.VITE_RUN_ENV || 'development';

/**
 * Initializing Coralogix RUM service for error tracking, performance monitoring, and bug detection
 */
export const initCoralogixRum = async (): Promise<void> => {
	try {
		// Skip initialization if the public key is not configured
		if (!coralogixPublicKey) {
			Logger.warn('Coralogix RUM: Public key not configured, skipping initialization');
			return;
		}

		const userInfo = await getUserMetadata();
		const tenantInfo = await getUserTenant();

		// Initialize Coralogix RUM SDK
		CoralogixRum.init({
			public_key: coralogixPublicKey,
			application: coralogixApplicationName,
			version: coralogixVersion,
			environment: coralogixEnvironment,
			coralogixDomain: 'US2',
			user_context: {
				user_id: userInfo?.id || 'unknown',
				user_name: userInfo?.name || 'unknown',
				user_email: userInfo?.email || 'unknown',
				user_metadata: {
					accountId: tenantInfo?.id || 'unknown'
				}
			},
			sessionSampleRate: coralogixEnvironment === 'production' ? 100 : 0,
			instrumentations: {
				errors: true,
				interactions: true,
				long_tasks: true,
				fetch: true,
				xhr: true,
				resources: true,
				web_vitals: true
			},
			sessionRecordingConfig: {
				enable: coralogixEnvironment === 'production', // Must declare.
				/**
				 * If autoStartSessionRecording is false, you can manually start & stop your session recording.
				 * Refer to Recording Manually Section.
				 * */
				autoStartSessionRecording: true, // Automatically records your session when SDK is up.
				recordConsoleEvents: true, // Will record all console events from dev tools. Levels: log, debug, warn, error, info, table etc..
				sessionRecordingSampleRate: 100 // Percentage of overall sessions recording being tracked, defaults to 100% and applied after the overall sessionSampleRate.
			}
		});

		Logger.info('Coralogix RUM initialized successfully', {
			application: coralogixApplicationName,
			version: coralogixVersion,
			environment: coralogixEnvironment
		});
	} catch (err) {
		Logger.error('Error initializing Coralogix RUM', err);
	}
};

/**
 * Log custom error to Coralogix RUM
 */
// eslint-disable-next-line
const logError = (error: Error, context?: Record<string, any>): void => {
	try {
		CoralogixRum.captureError(error, context);
	} catch (err) {
		Logger.error('Error logging to Coralogix RUM', err);
	}
};

/**
 * Add custom timing measurement for performance tracking
 */
// eslint-disable-next-line
const addCustomTiming = (name: string, duration?: number): void => {
	try {
		CoralogixRum.addTiming(name, duration);
	} catch (err) {
		Logger.error('Error adding custom timing to Coralogix RUM', err);
	}
};

/**
 * Send custom measurement (numeric value)
 */
// eslint-disable-next-line
const sendCustomMeasurement = (name: string, value: number): void => {
	try {
		CoralogixRum.sendCustomMeasurement(name, value);
	} catch (err) {
		Logger.error('Error sending custom measurement to Coralogix RUM', err);
	}
};
